import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch, withRouter } from 'react-router-dom';

import { AuthContext } from '../commons/auth/AuthContext';
import LoadingPage from '../commons/LoadingPage';
import { LoginForm } from '../commons/auth/LoginForm';
import ErrorPage from '../commons/ErrorPage';
import SessionExpired from '../commons/auth/SessionExpired';
import { ReportIssueContainer } from '../commons/report-issue/ReportIssueContainer';
import {
    getInvalidUrlMessageElement,
    retentionResponseReceivedMessageElement,
    consultationsWorldEmailLink,
} from '../commons/MessageComponents';
import {
    authenticateRetentionLink,
    reportIssueWithRetention,
    validateRetentionLink,
} from './retentionRoutes';
import { getUrlForRetentionForm, VIEW_ROUTES_URL } from '../commons/UrlUtils';
import { GenericMessagePage } from '../commons/GenericMessagePage';
import { CONSENT_LINK_URL_STATUS } from './RetentionConstants';

const RetentionFormDisplayContainer = (props) => {
    const { auth } = useContext(AuthContext);
    if (!auth.isAuthenticated) {
        return (
            <div className="mt-24">
                <LoginForm
                    authenticateLink={authenticateRetentionLink}
                    emailBasedAuthentication={true}
                />
            </div>
        );
    }

    const RetentionFormDisplay = props.render;
    return (
        <div>
            {auth.isExpired && (
                <SessionExpired
                    authenticateLink={authenticateRetentionLink}
                    emailBasedAuthentication={true}
                />
            )}
            <RetentionFormDisplay consultantCountry={props.consultantCountry} />
        </div>
    );
};

RetentionFormDisplayContainer.propTypes = {
    render: PropTypes.element,
    authenticateLink: PropTypes.func,
    consultantCountry: PropTypes.string,
};

const RetentionAuthAwareContainer = (props) => {
    // when the user loads the form, it is unauthenticated.
    const [auth, setAuth] = useState({ isAuthenticated: false });

    return (
        <AuthContext.Provider value={{ auth, setAuth }}>
            <ReportIssueContainer
                reportIssue={reportIssueWithRetention}
                emailLink={consultationsWorldEmailLink}
            />
            <RetentionFormDisplayContainer {...props} />
        </AuthContext.Provider>
    );
};

// TODO - Why mix hooks with withRouter
export const RetentionAuthenticateFormContainer = withRouter((props) => {
    const [linkStatus, setLinkStatus] = useState();
    const routeMatch = useRouteMatch();
    const history = useHistory();

    const { url } = routeMatch.params;
    const { path } = props.match;
    const { state } = props.location;

    const setInvoiceUrlStatusDetails = (urlStatusDetails) => {
        setLinkStatus(urlStatusDetails.status);
        props.setConsultantCountry(urlStatusDetails.country);
    };

    // if the redirectAfterResponseReceived prop is present in state, it implies that the redirection
    // is happening after successfully receiving the consent response - updating the linkStatus accordingly
    if (
        state?.redirectAfterResponseReceived &&
        linkStatus !== CONSENT_LINK_URL_STATUS.SUBMITTED
    ) {
        setLinkStatus(CONSENT_LINK_URL_STATUS.SUBMITTED);
    }

    // validate link when it changes.
    useEffect(() => {
        validateRetentionLink(url, setInvoiceUrlStatusDetails);
    }, [url]);

    switch (linkStatus) {
        case CONSENT_LINK_URL_STATUS.VALID:
            if (path !== VIEW_ROUTES_URL.RETENTION_FORM) {
                // TODO - What's the point of re-pushing the url, didn't we verify the pattern as part of router
                history.push(getUrlForRetentionForm(url));
            }
            return <RetentionAuthAwareContainer {...props} />;
        case CONSENT_LINK_URL_STATUS.SUBMITTED:
            return (
                <GenericMessagePage
                    message={'Submission successful.'}
                    subMessage={retentionResponseReceivedMessageElement}
                />
            );
        case CONSENT_LINK_URL_STATUS.INVALID:
            return (
                <ErrorPage
                    message={'Invalid URL'}
                    subMessage={getInvalidUrlMessageElement(
                        consultationsWorldEmailLink
                    )}
                />
            );
        case CONSENT_LINK_URL_STATUS.EXPIRED:
            return (
                <ErrorPage
                    message={'The link you entered has expired.'}
                    subMessage={getInvalidUrlMessageElement(
                        consultationsWorldEmailLink
                    )}
                />
            );
        default:
            return <LoadingPage />;
    }
});

RetentionAuthenticateFormContainer.propTypes = {
    consultantCountry: PropTypes.string,
    setConsultantCountry: PropTypes.func,
};
